/**
 * `<DialogScrim>` — the ONE renderer of `.modal-overlay`.
 *
 * MODALCHROME-BURNDOWN-001 Q-L2-4 (owner ruling, 2026-09-20).
 *
 * ## The problem it solves, which is not the obvious one
 *
 * `web/no-local-modal-chrome` bans `className="modal-overlay"` and tells you to use page-shell's
 * `<Modal>` instead. That instruction is complete for almost every file in the estate — and
 * impossible for three packages. `<AppShell>` composes `command-palette`, `keyboard-shortcuts` and
 * `location-picker`, so page-shell DEPENDS ON them; importing `<Modal>` back is a dependency cycle.
 *
 * Two of those three had no backdrop, so taking `useModalChrome` cleared the rule for them.
 * `location-picker` renders a scrim behind its mobile bottom sheet, and for it "adopt the chrome"
 * did NOT clear the rule: the class is the violation, and the only sanctioned renderer of that
 * class was a file it cannot import.
 *
 * ## Why a component and not an exemption
 *
 * The rule exempts `Modal.jsx` **by PATH**, so the class was never really forbidden — it was
 * forbidden *everywhere except one file*. Adding location-picker's two files to that exemption
 * list would have made three hand-renderers of one class and left the exemption list growing,
 * which is the thing this initiative exists to shrink.
 *
 * Owning it here inverts that: `.modal-overlay` has exactly ONE renderer in the repo, `<Modal>`
 * composes it like everyone else, and the rule's path exemption points at a file that genuinely is
 * the primitive. The leaf can be imported from anywhere because its whole cone is React.
 *
 * ## What it deliberately does NOT do
 *
 * No portal, no scroll lock, no focus handling — `useModalChrome` owns all of that, and a scrim
 * that also managed focus would be a second chrome. This renders one element, puts the backdrop
 * handler on it, and gets out of the way.
 *
 * @module @puffin-core/dialog-chrome/DialogScrim
 */

import PropTypes from 'prop-types';

/**
 * @typedef {object} DialogScrimOwnProps
 * @property {React.MouseEventHandler<HTMLDivElement>} [onBackdropClick] - Usually `handleBackdropClick`
 *   from `useModalChrome`, which already checks `target === currentTarget` so a click INSIDE the dialog
 *   does not close it.
 * @property {string} [className] - Appended after `modal-overlay`, for a surface that needs a
 *   positioning modifier (the mobile sheet). Never a replacement: the base class is not optional,
 *   because it is what `modals.css` styles and what the rule recognises.
 * @property {React.ReactNode} [children] - The dialog itself — OPTIONAL, because a DRAWER's scrim is a
 *   SIBLING of its panel rather than its parent. Nesting a fixed-position panel inside the scrim would
 *   make every click inside the panel bubble to the backdrop handler and dismiss the drawer, so those
 *   surfaces render a childless scrim beside the panel.
 * @property {string} [testId]
 */

/**
 * The remaining props are spread onto the scrim element, so they are a div's attributes — except
 * `onClick`, which the spread would let REPLACE `onBackdropClick`.
 *
 * @typedef {DialogScrimOwnProps & Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'>} DialogScrimProps
 */

/**
 * @param {DialogScrimProps} props
 * @returns {React.JSX.Element}
 */
export function DialogScrim({ onBackdropClick, className, children, testId, ...rest }) {
  return (
    <div
      className={className ? `modal-overlay ${className}` : 'modal-overlay'}
      onClick={onBackdropClick}
      data-testid={testId}
      // `presentation`, deliberately: the scrim is a visual layer, and the dialog inside it carries
      // the real role. Announcing a generic group around every dialog would put a meaningless node
      // in every screen-reader tree in the fleet.
      role="presentation"
      {...rest}
    >
      {children}
    </div>
  );
}

DialogScrim.propTypes = {
  onBackdropClick: PropTypes.func,
  className: PropTypes.string,
  // NOT `isRequired`: a drawer's scrim has no children (see the `children` param note). React 19
  // does not validate propTypes at runtime, so an inaccurate declaration here would never fail —
  // which is exactly why it has to be accurate.
  children: PropTypes.node,
  testId: PropTypes.string,
};

export default DialogScrim;
